<template>
  <div class="return-received">
    <div>
      <div class="d-flex align-items-center">
        <div>
          <h1 class="page-title">
            <a-icon type="arrow-left" @click="goBack" />
            {{ $t('returnReceived.title') }}
          </h1>
        </div>
      </div>
    </div>
    <a-form-model
      ref="ruleForm"
      :model="form"
      :rules="isDetail ? {} : rules"
      :class="{ info: isDetail }"
    >
      <a-card :title="$t('returnReceived.detailCard')" class="w-100 mt-4" :bordered="false">
        <a-row :gutter="24">
          <a-col :span="10">
            <a-form-model-item :label="$t('returnReceived.returnNumber')" prop="order_number">
              <a-input
                v-model.trim="form.order_number"
                size="large"
                autocomplete="off"
                :placeholder="$t('returnReceived.returnNumber')"
                :disabled="isDisabled"
                allow-clear
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="10">
            <a-form-model-item :label="$t('returnReceived.returnDate')" prop="date">
              <a-date-picker
                v-model="form.date"
                show-time
                size="large"
                class="w-100"
                :placeholder="$t('returnReceived.returnDate')"
                :format="dateFormat"
                :disabled="isDisabled"
              />
            </a-form-model-item>
          </a-col>
        </a-row>
        <a-row :gutter="24">
          <a-col :span="10">
            <a-form-model-item :label="$t('fulfillment.orderNo')" prop="number">
              <a-select
                v-model="form.number"
                size="large"
                class="select-antd-default"
                style="width: 100%"
                :placeholder="$t('fulfillment.orderNo')"
                show-search
                :label-in-value="false"
                :not-found-content="fetching ? undefined : null"
                :filter-option="false"
                :disabled="isDisabled"
                @change="handleChangeDocument"
                @blur="handleResetSearch"
                @search="handleSearch"
              >
                <a-spin v-if="fetching" slot="notFoundContent" size="small" />
                <a-select-option
                  v-for="item in fulfillments"
                  :key="item.fulfillment_id"
                  :value="`${item.fulfillment_id}.${item.order_id}.${item.order_number}`"
                >
                  <div class="d-flex flex-column product-select">
                    <div>{{ item?.order_number }}</div>
                    <div class="sku-desc">
                      {{ $t('returnReceived.storeLabel') + ' : ' + item?.channel_name }}
                    </div>
                  </div>
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="10">
            <a-form-model-item :label="$t('sideBar.warehouse')" prop="warehouse_id" class="mb-0">
              <a-select
                v-model="form.warehouse_id"
                size="large"
                class="select-antd-default"
                :placeholder="$t('sideBar.warehouse')"
                disabled
              >
                <a-select-option
                  v-for="item in warehouseList"
                  :key="item.value"
                  :value="item.value"
                >
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
        </a-row>
      </a-card>
      <a-card :title="$t('returnReceived.itemCard')" class="w-100 mt-4" :bordered="false">
        <a-row :gutter="24">
          <a-col :span="24">
            <div>
              <div class="d-flex align-items-center justify-content-between mb-3">
                <div>{{ $t('returnReceived.totalSku') }}: <b>{{ computedValidSKU || 0 }}</b></div>
              </div>
              <a-table
                :columns="columns"
                :row-key="(record, index) => index"
                :data-source="form.items"
                :loading="isLoading"
                :pagination="false"
                :scroll="{ y: 330 }"
              >
                <template slot="sku" slot-scope="text, record">
                  <div class="d-flex flex-column product-select">
                    <div>{{ record?.catalog_title }}</div>
                    <div class="sku-desc">
                      {{ $t('productAvailability.sku') + ' : ' + text }}
                    </div>
                  </div>
                </template>
              </a-table>
            </div>
          </a-col>
        </a-row>
      </a-card>
      <div v-if="!isDetail" class="discount-footer">
        <a-button
          type="primary"
          size="large"
          class="mr-4"
          ghost
          :disabled="isLoading"
          @click="goBack"
        >
          {{ $t('utils.cancel') }}
        </a-button>
        <a-button
          type="primary"
          size="large"
          :loading="isLoading"
          @click="onSubmitReturn"
        >
          {{ $route.query.discount_id && !isDuplicate ? $t('utils.edit') : $t('utils.save') }}
        </a-button>
      </div>
    </a-form-model>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { getFulfillmentList, getFulfillmentDetail } from '@/api/fulfillment'
import { getReturnReceivedDetail, getCatalogListGR, getUom, addReturnReceived } from '@/api/inventory'
import { getWarehouseInternal } from '@/api/warehouse'
import { useInputNumber } from '@/composable/useInputNumber'
import { useDateTime } from '@/composable/useDateTime'

export default {
  name: 'CreateReturnReceived',
  setup() {
    const { numberOnly, toNumber } = useInputNumber()
    const { parseISODate } = useDateTime()
    return { numberOnly, toNumber, parseISODate }
  },
  data: function () {
    return {
      search: '',
      dateFormat: 'DD MMM YYYY HH:mm',
      isDetail: false,
      isLoading: false,
      fetching: false,
      fulfillments: [],
      warehouseList: [],
      rules: {
        number: [{ required: true, message: this.$t('returnReceived.refDocRequired'), trigger: 'change' }],
        warehouse_id: [{ required: true, message: this.$t('returnReceived.warehouseRequired'), trigger: 'change' }],
        order_number: [{ required: true, message: this.$t('returnReceived.returnNoRequired'), trigger: 'blur' }],
      },
      form: {
        fulfillment_id: '',
        order_id: '',
        order_number: '',
        number: undefined,
        date: null,
        warehouse_id: '',
        items: [],
      },
    }
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t('fulfillment.product'),
          dataIndex: 'sku',
          key: 'sku',
          scopedSlots: {
            customRender: 'sku',
          },
        },
        {
          title: this.$t('utils.quantity'),
          dataIndex: 'quantity',
          key: 'quantity',
          width: 250,
          scopedSlots: {
            customRender: 'quantity',
          },
        },
        {
          title: this.$t('utils.uom'),
          dataIndex: 'uom',
          key: 'uom',
          width: 250,
          scopedSlots: {
            customRender: 'uom',
          },
        },
        {
          title: this.$t('returnReceived.stockType'),
          dataIndex: 'stock_type',
          key: 'stock_type',
          width: 150,
        },
      ]
    },
    businessId() {
      return this.$store.state.user.restriction_base === 'Warehouse'
        ? Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
        : this.$route.query.business_id
    },
    computedValidSKU() {
      return this.form.items.filter((obj) => obj.catalog_id)?.length
    },
    isDisabled() {
      if (this.isDetail) {
        return true
      } else {
        return this.isLoading
      }
    },
  },
  watch: {
    businessId() {
      this.init()
    },
    search: {
      deep: true,
      immediate: true,
      handler: function () {
        this.fetchFulfillment()
      },
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.getWarehouseList()
      if (this.$route.query.id && this.$route.query.number_return) {
        this.isDetail = true
        this.fetchReturnReceivedDetail()
      } else {
        this.fetchFulfillment()
      }
    },
    getWarehouseList: debounce(async function() {
      return await getWarehouseInternal({
        business_id: this.businessId,
        limit: 500,
      })
        .then(response => {
          const raw = response?.data?.data || response?.data || []
          this.warehouseList = raw.map(w => ({
            ...w,
            value: String(w.id || w.warehouse_id),
            label: String(w.name || w.warehouse_name || '').trim(),
          }))
          this.form.warehouse_id = this.$route.query.warehouseId
        })
        .catch(err => {
          console.error(err)
          return err
        })
    }, 500),
    fetchFulfillment: debounce(async function() {
      this.fetching = true
      const params = {
        business_id: this.businessId,
        warehouse_id: this.$route.query.warehouseId,
        delivery_order_states: 'SHIPPED,DELIVERED,BUYER_ACCEPTED',
        q: this.search,
        page: 1,
        limit: 20,
      }
      await getFulfillmentList({ url: 'fulfillment', params })
        .then(({ data: { data: response } }) => {
          this.fulfillments = response
        })
        .catch(err => {
          this.fulfillments = []
          console.error(err)
        })
        .finally(() => this.fetching = false)
    }, 500),
    fetchReturnReceivedDetail: debounce(async function() {
      this.isLoading = true
      await getReturnReceivedDetail({
        business_id: this.businessId,
        id: this.$route.query.id,
      })
      .then(async ({ data: response }) => {
        const { fulfillment_id, order_id, order_number, number, date, warehouse_id, items } = response
        this.form = {
          fulfillment_id,
          order_id,
          order_number,
          number,
          date,
          warehouse_id,
          items: items && items.length
            ? await Promise.all(items.map(async (item, index) => {
                const { id, quantity, uom, sku, batch_number, expired_date, stock_type, serial_number } = item
                const tempItem = {
                  item_reference_id: index + 1,
                  catalog_id: id,
                  catalog_title: '',
                  quantity,
                  uom,
                  sku,
                  batch_number,
                  expired_date,
                  stock_type,
                  serial_number,
                }
                await getCatalogListGR({
                  business_id: this.businessId,
                  warehouse_id,
                  search: sku,
                  page: 1,
                  limit: 20,
                })
                .then(({ data: { data: responseCatalog } }) => {
                  responseCatalog?.forEach(inventory => {
                    if (inventory.sku === sku) {
                      tempItem.catalog_title = inventory.title
                    }
                  })
                })
                return tempItem
              }))
            : [],
        }
      })
      .catch(err => console.error(err))
      .finally(() => this.isLoading = false)
    }, 500),
    async handleChangeDocument(value) {
      this.fetching = true
      await getFulfillmentDetail({
        url: 'fulfillment',
        fulfillment_id: value.split('.')[0],
      })
      .then(({ data: { data: responseFulfillment } }) => {
        let index = 0
        let itemList = []
        responseFulfillment?.fulfillments?.forEach(fulfillment => {
          fulfillment?.line_items?.forEach(async item => {
            await getCatalogListGR({
              business_id: this.businessId,
              warehouse_id: this.$route.query.warehouseId,
              search: item.sku,
              page: 1,
              limit: 20,
            })
            .then(({ data: { data: responseCatalog } }) => {
              responseCatalog?.forEach(async inventory => {
                if (inventory.sku === item.sku) {
                  itemList.push({
                    item_reference_id: index + 1,
                    catalog_id: `${inventory.id}.${inventory.product_id}`,
                    catalog_title: inventory.title,
                    quantity: item.quantity,
                    uom: inventory.unit,
                    sku: inventory.sku,
                    batch_number: '',
                    expired_date: null,
                    stock_type: 'AV',
                    serial_number: '',
                  })
                  await getUom({
                    id: inventory.product_id,
                    params: {
                      business_id: this.businessId,
                      page: 1,
                      limit: 1000,
                    },
                  })
                  .then(({ data: { data: responseUom } }) => {
                    let levels = []
                    responseUom?.units?.forEach((i, idx) => {
                      levels.push(i.unit)
                      itemList[index].units = {
                        [i.unit]: {
                          numerator: i.numerator,
                          denumerator: i.denumerator,
                        },
                      }
                      itemList[index].base_quantity = +item.quantity * +i.numerator

                      if(idx == 0) itemList[index].uom = i.unit
                      if(i.numerator == 1 && i.denumerator == 1) itemList[index].base_uom = i.unit
                    })
                    itemList[index].levels = levels
                  })
                  .catch(err => console.error(err))
                  .finally(() => index++)
                }
              })
            })
            .catch(err => console.error(err))
          })
        })
        this.form.items = itemList
      })
      .catch(err => {
        this.form.items = []
        console.error(err)
      })
      .finally(() => this.fetching = false)
    },
    onSubmitReturn() {
      this.$refs.ruleForm.validate(async valid => {
        if (valid) {
          this.isLoading = true
          const arrValue = this.form.number ? this.form.number.split('.') : null
          await addReturnReceived({
            business_id: this.businessId,
            data: {
              ...this.form,
              fulfillment_id: arrValue?.length ? arrValue[0] : null,
              order_id: arrValue?.length ? arrValue[1] : null,
              number: arrValue?.length ? arrValue[2]: null,
              items: this.form.items.map(item => {
                const { catalog_id, sku, quantity, uom, batch_number, serial_number, expired_date, stock_type } = item
                return {
                  catalog_id: catalog_id.split('.')[0],
                  sku,
                  quantity,
                  uom,
                  batch_number,
                  serial_number,
                  expired_date,
                  stock_type,
                }
              }),
            },
          })
          .then(() => {
            this.$notification.success({ message: this.$t('utils.success_save') })
            this.goBack()
          })
          .catch(err => {
            this.$notification.error({ message: err?.response?.data?.message || this.$t('utils.failed_save') })
            console.error(err)
          })
          .finally(() => this.isLoading = false)
        }
      })
    },
    handleSearch: debounce(function(e) {
      this.search = e
    }, 500),
    handleResetSearch() {
      this.search = ''
    },
    goBack() {
      this.$router.back({
        path: '/inventory/return-received',
        query: { ...this.$route.query, warehouseId: undefined },
      })
    },
  },
}
</script>

<style lang="scss">
@import './create.module.scss';
</style>
<style lang="scss" scoped>
.ant-select-lg .ant-select-selection--single .product-select {
  white-space: nowrap !important;
}
.product-select {
  white-space: pre-wrap !important;
  .sku-desc {
    font-size: 12px;
    font-weight: 500;
  }
}
</style>
